import { useEffect, useState } from 'react';
import { isOnline } from '@/lib/connectivity';

const CONNECTIVITY_EVENTS = ['online', 'offline', 'server-reachable', 'server-unreachable'] as const;

/**
 * React view of `isOnline()` from src/lib/connectivity: the browser reports a
 * connection AND the Mon Ami server has answered a request.
 *
 * The value is re-derived from `isOnline()` on every browser and connectivity
 * event instead of being set from the event itself. The browser `online`
 * event only says a network interface came up — a client's wifi with no route
 * out, or a captive portal, fires it while the server is still unreachable.
 */
export function useServerOnline(): boolean {
  const [online, setOnline] = useState(() => isOnline());

  useEffect(() => {
    const refresh = () => setOnline(isOnline());
    CONNECTIVITY_EVENTS.forEach((name) => window.addEventListener(name, refresh));
    // Re-read after subscribing: a verdict that landed between the initial
    // render and this effect would otherwise be missed until the next event.
    refresh();
    return () => CONNECTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, refresh));
  }, []);

  return online;
}
